import React, { useState, useEffect, useRef } from 'react';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { DollarSign, TrendingDown, TrendingUp, CreditCard, FileText, Briefcase } from 'lucide-react';
import { useFacilityStore } from '../../../../../app/z-store/store';

const AccountsDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [glEntries, setGlEntries] = useState([]);
    const [financialStats, setFinancialStats] = useState({
        totalRevenue: 0,
        totalExpenses: 0,
        netIncome: 0,
        accountsReceivable: 0,
        accountsPayable: 0,
        cashOnHand: 0
    });
    const [revenueData, setRevenueData] = useState({});
    const [expenseData, setExpenseData] = useState({});
    const [cashFlowData, setCashFlowData] = useState({});
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const selectedFacilityId = useFacilityStore(state => state.facilityId);
    const toast = useRef(null);

    const periods = [
        { label: 'This Month', value: 'month' },
        { label: 'This Quarter', value: 'quarter' },
        { label: 'This Year', value: 'year' },
        { label: 'Last 12 Months', value: 'last12Months' }
    ];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchData();
        }
    }, [selectedFacilityId, selectedPeriod]);

    const fetchData = async () => {
        try {
            setLoading(true);
            await fetchAccounts();
            await fetchGLEntries();
        } catch (err) {
            setError('Failed to fetch financial data');
            console.error(err);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to fetch financial data: ' + (err.message || 'Unknown error')
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            console.log('Fetching accounts for facility:', selectedFacilityId);
            const response = await makeRequest2(`/api/v1/gl-accounts/${selectedFacilityId}`, 'GET');
            console.log('Raw accounts API response:', response);

            // Handle nested data structure
            let fetchedAccounts = [];
            if (response?.data?.data) {
                fetchedAccounts = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
            } else if (response?.data) {
                fetchedAccounts = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedAccounts = response;
            }

            console.log('Processed accounts:', fetchedAccounts);
            setAccounts(fetchedAccounts);
            return fetchedAccounts;
        } catch (error) {
            console.error('Error fetching accounts:', error);
            throw error;
        }
    };

    const fetchGLEntries = async () => {
        try {
            console.log('Fetching GL entries for facility:', selectedFacilityId);
            const response = await makeRequest2(`/api/v1/gl-entries/get-all-entries/${selectedFacilityId}`, 'GET');
            console.log('Raw GL entries API response:', response);

            let fetchedEntries = [];
            if (response?.data) {
                fetchedEntries = Array.isArray(response.data) ? response.data : [response.data];
            } else if (Array.isArray(response)) {
                fetchedEntries = response;
            }

            console.log('Processed GL entries:', fetchedEntries);
            setGlEntries(fetchedEntries);

            processFinancialData(accounts, fetchedEntries);
            return fetchedEntries;
        } catch (error) {
            console.error('Error fetching GL entries:', error);
            throw error;
        }
    };

    // Process the account and GL entry data to generate financial statistics and charts
    const processFinancialData = (accountsData, entriesData) => {
        if (!accountsData || accountsData.length === 0 || !entriesData || entriesData.length === 0) {
            console.log('No data to process');
            return;
        }

        // Filter entries by period
        const filteredEntries = filterEntriesByPeriod(entriesData);

        // Create a map for quick account lookup
        const accountMap = accountsData.reduce((map, account) => {
            map[account._id] = account;
            return map;
        }, {});

        // Calculate financial statistics
        const stats = calculateFinancialStats(accountsData, filteredEntries, accountMap);
        setFinancialStats(stats);

        // Generate chart data
        const revData = generateRevenueChartData(filteredEntries, accountMap);
        setRevenueData(revData);

        const expData = generateExpenseChartData(filteredEntries, accountMap);
        setExpenseData(expData);

        const cfData = generateCashFlowChartData(filteredEntries, accountMap);
        setCashFlowData(cfData);
    };

    // Filter GL entries based on selected period
    const filterEntriesByPeriod = (entries) => {
        if (!entries || entries.length === 0) return [];

        const now = new Date();
        let startDate;

        switch (selectedPeriod) {
            case 'month':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                break;
            case 'quarter':
                startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
                break;
            case 'year':
                startDate = new Date(now.getFullYear(), 0, 1);
                break;
            case 'last12Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
                break;
            default:
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }

        return entries.filter(entry => new Date(entry.entryDate) >= startDate);
    };

    // Calculate financial statistics based on accounts and GL entries, only considering credits
    const calculateFinancialStats = (accounts, entries, accountMap) => {
        // Initialize values
        let totalRevenue = 0;
        let totalExpenses = 0;
        let accountsReceivable = 0;
        let accountsPayable = 0;
        let cashOnHand = 0;

        // Create a map to track totals by account type
        const accountTypeTotals = {
            'Asset': 0,
            'Liability': 0,
            'Equity': 0,
            'Revenue': 0,
            'Expense': 0
        };

        // Calculate totals by account type from credits only
        entries.forEach(entry => {
            if (entry.entryType !== 'credit') return; // Only process credit entries

            const account = accountMap[entry.accountId];
            if (!account) return;

            if (accountTypeTotals.hasOwnProperty(account.accountType)) {
                accountTypeTotals[account.accountType] += entry.amount;
            }

            // Track specific accounts for our financial metrics
            if (account.accountType === 'Revenue') {
                totalRevenue += entry.amount;
            }
        });

        // Use account balances for more accurate reporting
        accounts.forEach(account => {
            // Track expenses from expense accounts
            if (account.accountType === 'Expense') {
                totalExpenses += account.accountBalance;
            }

            // Track specific account types
            if (account.accountName.toLowerCase().includes('receivable')) {
                accountsReceivable += account.accountBalance;
            } else if (account.accountName.toLowerCase().includes('payable')) {
                accountsPayable += account.accountBalance;
            } else if (account.accountName.toLowerCase().includes('cash') ||
                account.accountName.toLowerCase().includes('bank')) {
                cashOnHand += account.accountBalance;
            }
        });

        // In case we didn't find specific named accounts, use account types
        if (accountsReceivable === 0) {
            accounts.forEach(account => {
                if (account.accountType === 'Asset' && account.isFinal) {
                    accountsReceivable += account.accountBalance;
                }
            });
        }

        if (accountsPayable === 0) {
            accounts.forEach(account => {
                if (account.accountType === 'Liability' && account.isFinal) {
                    accountsPayable += account.accountBalance;
                }
            });
        }

        if (cashOnHand === 0 && accountTypeTotals['Asset'] > 0) {
            // If no specific cash accounts were found, estimate based on Asset total
            cashOnHand = accountTypeTotals['Asset'] * 0.15; // Rough estimate
        }

        // Calculate net income
        const netIncome = totalRevenue - totalExpenses;

        return {
            totalRevenue,
            totalExpenses,
            netIncome,
            accountsReceivable,
            accountsPayable,
            cashOnHand
        };
    };

    // Generate data for revenue trend chart - only considering credits
    const generateRevenueChartData = (entries, accountMap) => {
        // Determine labels based on selected period
        const labels = getLabelsForPeriod();

        // Initialize dataset with zero values
        const data = Array(labels.length).fill(0);

        // Process entries to get revenue by period (credits only)
        entries.forEach(entry => {
            // Skip if not a credit entry
            if (entry.entryType !== 'credit') return;

            const account = accountMap[entry.accountId];
            if (!account) return;

            // Only process Revenue accounts
            if (account.accountType === 'Revenue') {
                const entryDate = new Date(entry.entryDate);
                let index;

                // Determine which period bucket this entry belongs in
                if (selectedPeriod === 'month') {
                    // For month view, group by week
                    const dayOfMonth = entryDate.getDate();
                    index = Math.min(Math.floor(dayOfMonth / 7), labels.length - 1);
                } else if (selectedPeriod === 'quarter' || selectedPeriod === 'year' || selectedPeriod === 'last12Months') {
                    // For quarter/year/last12Months, group by month
                    const monthIndex = entryDate.getMonth();
                    const currentMonth = new Date().getMonth();

                    if (selectedPeriod === 'quarter') {
                        index = (monthIndex + 12 - currentMonth) % 3;
                    } else {
                        index = (monthIndex + 12 - currentMonth) % 12;
                    }
                }

                // Add to the appropriate bucket (credits only)
                if (index >= 0 && index < data.length) {
                    data[index] += entry.amount;
                }
            }
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Revenue',
                    data,
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    borderColor: 'rgb(75, 192, 192)',
                    borderWidth: 2,
                    tension: 0.4
                }
            ]
        };
    };

    // Generate data for expense breakdown chart - only considering credit entries
    const generateExpenseChartData = (entries, accountMap) => {
        // Count the occurrences of each expense type
        const expenseTypes = {};

        // First, group expenses by account name
        accounts.forEach(account => {
            if (account.accountType === 'Expense' && account.accountBalance > 0) {
                expenseTypes[account.accountName] = account.accountBalance;
            }
        });

        // If we don't have enough expense accounts with balances,
        // supplement with credit entries related to expenses
        if (Object.keys(expenseTypes).length < 3) {
            entries.forEach(entry => {
                // Only consider credit entries
                if (entry.entryType !== 'credit') return;

                const account = accountMap[entry.accountId];
                if (!account) return;

                // Only process Expense accounts or specific expense descriptions
                if (account.accountType === 'Expense' ||
                    entry.description.toLowerCase().includes('purchase') ||
                    entry.description.toLowerCase().includes('expense')) {

                    const category = account.accountName || entry.description || 'Other';

                    if (!expenseTypes[category]) {
                        expenseTypes[category] = 0;
                    }

                    expenseTypes[category] += entry.amount;
                }
            });
        }

        // Convert to arrays for chart
        const labels = Object.keys(expenseTypes);
        const data = Object.values(expenseTypes);

        // If no expense data, provide default categories
        if (labels.length === 0) {
            return {
                labels: ['Salaries', 'Rent', 'Utilities', 'Marketing', 'Equipment', 'Other'],
                datasets: [
                    {
                        label: 'Expenses',
                        data: [0, 0, 0, 0, 0, 0],
                        backgroundColor: [
                            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
                        ]
                    }
                ]
            };
        }

        return {
            labels,
            datasets: [
                {
                    label: 'Expenses',
                    data,
                    backgroundColor: [
                        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
                        '#FF8C00', '#8A2BE2', '#3CB371', '#DC143C', '#20B2AA', '#B8860B'
                    ].slice(0, labels.length)
                }
            ]
        };
    };

    // Generate data for cash flow chart based on account types and credit entries
    const generateCashFlowChartData = (entries, accountMap) => {
        // Determine labels based on selected period
        const labels = getLabelsForPeriod();

        // Initialize datasets with zero values
        const cashIn = Array(labels.length).fill(0);
        const cashOut = Array(labels.length).fill(0);

        // Process entries to get cash flow by period - focus on credit entries
        entries.forEach(entry => {
            const account = accountMap[entry.accountId];
            if (!account) return;

            const entryDate = new Date(entry.entryDate);
            let index;

            // Determine which period bucket this entry belongs in
            if (selectedPeriod === 'month') {
                // For month view, group by week
                const dayOfMonth = entryDate.getDate();
                index = Math.min(Math.floor(dayOfMonth / 7), labels.length - 1);
            } else if (selectedPeriod === 'quarter' || selectedPeriod === 'year' || selectedPeriod === 'last12Months') {
                // For quarter/year/last12Months, group by month
                const monthIndex = entryDate.getMonth();
                const currentMonth = new Date().getMonth();

                if (selectedPeriod === 'quarter') {
                    index = (monthIndex + 12 - currentMonth) % 3;
                } else {
                    index = (monthIndex + 12 - currentMonth) % 12;
                }
            }

            // Add to the appropriate bucket based on account type and credit entries
            if (index >= 0 && index < cashIn.length && entry.entryType === 'credit') {
                if (account.accountType === 'Revenue' || account.accountType === 'Asset') {
                    cashIn[index] += entry.amount;
                } else if (account.accountType === 'Expense' || account.accountType === 'Liability') {
                    cashOut[index] += entry.amount;
                }
            }
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Cash In',
                    data: cashIn,
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    borderColor: 'rgb(54, 162, 235)',
                    borderWidth: 2
                },
                {
                    label: 'Cash Out',
                    data: cashOut,
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    borderColor: 'rgb(255, 99, 132)',
                    borderWidth: 2
                }
            ]
        };
    };

    // Helper function to get appropriate labels based on selected period
    const getLabelsForPeriod = () => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentMonth = new Date().getMonth();

        switch (selectedPeriod) {
            case 'month':
                return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
            case 'quarter':
                return months.slice(currentMonth - 2, currentMonth + 1);
            case 'year':
            case 'last12Months':
                // Last 12 months in correct order
                return [...months.slice(currentMonth + 1), ...months.slice(0, currentMonth + 1)];
            default:
                return ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
        }
    };

    // Format number as currency (KES)
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(value) + " KES";
    };

    const getChartOptions = (title) => {
        return {
            plugins: {
                title: {
                    display: true,
                    text: title,
                    font: {
                        size: 16
                    }
                },
                legend: {
                    position: 'bottom'
                }
            },
            responsive: true,
            maintainAspectRatio: false
        };
    };

    if (loading) {
        return <div className="p-4 text-center">Loading financial data...</div>;
    }

    if (error) {
        return <div className="p-4 text-center text-danger">{error}</div>;
    }

    return (
        <div className="accounts-dashboard">
            <Toast ref={toast} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>Financial Overview</h4>
                <Dropdown
                    value={selectedPeriod}
                    options={periods}
                    onChange={(e) => setSelectedPeriod(e.value)}
                    placeholder="Select Period"
                    className="w-auto"
                />
            </div>

            {/* Stats Cards */}
            <div className="row mb-4">
                <div className="col-md-4 mb-3">
                    <Card className="h-100 shadow-sm">
                        <div className="text-center">
                            <div className="d-flex justify-content-center mb-2">
                                <DollarSign size={24} className="text-success" />
                            </div>
                            <h6 className="text-muted">Total Revenue</h6>
                            <h3 className="text-success">{formatCurrency(financialStats.totalRevenue)}</h3>
                        </div>
                    </Card>
                </div>
                <div className="col-md-4 mb-3">
                    <Card className="h-100 shadow-sm">
                        <div className="text-center">
                            <div className="d-flex justify-content-center mb-2">
                                <TrendingDown size={24} className="text-danger" />
                            </div>
                            <h6 className="text-muted">Total Expenses</h6>
                            <h3 className="text-danger">{formatCurrency(financialStats.totalExpenses)}</h3>
                        </div>
                    </Card>
                </div>
                <div className="col-md-4 mb-3">
                    <Card className="h-100 shadow-sm">
                        <div className="text-center">
                            <div className="d-flex justify-content-center mb-2">
                                <TrendingUp size={24} className={financialStats.netIncome >= 0 ? "text-success" : "text-danger"} />
                            </div>
                            <h6 className="text-muted">Net Income</h6>
                            <h3 className={financialStats.netIncome >= 0 ? "text-success" : "text-danger"}>
                                {formatCurrency(financialStats.netIncome)}
                            </h3>
                        </div>
                    </Card>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-md-4 mb-3">
                    <Card className="h-100 shadow-sm">
                        <div className="text-center">
                            <div className="d-flex justify-content-center mb-2">
                                <CreditCard size={24} className="text-primary" />
                            </div>
                            <h6 className="text-muted">Accounts Receivable</h6>
                            <h3>{formatCurrency(financialStats.accountsReceivable)}</h3>
                        </div>
                    </Card>
                </div>
                <div className="col-md-4 mb-3">
                    <Card className="h-100 shadow-sm">
                        <div className="text-center">
                            <div className="d-flex justify-content-center mb-2">
                                <FileText size={24} className="text-warning" />
                            </div>
                            <h6 className="text-muted">Accounts Payable</h6>
                            <h3>{formatCurrency(financialStats.accountsPayable)}</h3>
                        </div>
                    </Card>
                </div>
                <div className="col-md-4 mb-3">
                    <Card className="h-100 shadow-sm">
                        <div className="text-center">
                            <div className="d-flex justify-content-center mb-2">
                                <Briefcase size={24} className="text-info" />
                            </div>
                            <h6 className="text-muted">Cash on Hand</h6>
                            <h3>{formatCurrency(financialStats.cashOnHand)}</h3>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Charts */}
            <div className="row">
                <div className="col-md-8 mb-4">
                    <Card className="h-100 shadow-sm">
                        <div style={{ height: '300px' }}>
                            <Chart
                                type="line"
                                data={revenueData}
                                options={getChartOptions('Revenue Trend')}
                            />
                        </div>
                    </Card>
                </div>
                <div className="col-md-4 mb-4">
                    <Card className="h-100 shadow-sm">
                        <div style={{ height: '300px' }}>
                            <Chart
                                type="pie"
                                data={expenseData}
                                options={getChartOptions('Expense Breakdown')}
                            />
                        </div>
                    </Card>
                </div>
            </div>

            <div className="row">
                <div className="col-12 mb-4">
                    <Card className="shadow-sm">
                        <div style={{ height: '300px' }}>
                            <Chart
                                type="bar"
                                data={cashFlowData}
                                options={getChartOptions('Cash Flow')}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default AccountsDashboard;